/**
 * @file     FormConsentLine.tsx
 * @purpose  The one consent line under a lead or account form's submit button: Terms and Privacy. Email and SMS consent are the separate ConsentChoices boxes.
 */
'use client';

import { Fragment, type ReactNode } from 'react';
import Link from 'next/link';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { SEMANTIC_CSS_VARS } from '@/design-system/tokens/semanticColors';
import { routes } from '@/core/constants/routes';
import { FORM_CONSENT_COPY } from '@/core/config/smsConsent';

type Props = {
  /** The button's verb, as in "By continuing". */
  action?: string;
  sx?: SxProps<Theme>;
};

const LINK_TAG = /<(terms|privacy)>(.*?)<\/\1>/g;
const HREF = { terms: routes.terms, privacy: routes.privacy } as const;

function withConsentLinks(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(LINK_TAG)) {
    out.push(text.slice(last, match.index));
    const tag = match[1] as keyof typeof HREF;
    out.push(
      <Link key={`${tag}-${match.index}`} href={HREF[tag]} className="form-consent-link">
        {match[2]}
      </Link>,
    );
    last = (match.index ?? 0) + match[0].length;
  }
  out.push(text.slice(last));
  return out.map((node, i) => <Fragment key={i}>{node}</Fragment>);
}

export function FormConsentLine({ action, sx }: Props) {
  const { t } = useTranslation();
  const verb = action ?? t('forms.consent.continuing', { defaultValue: 'continuing' });
  const text = t('forms.consent.terms', { defaultValue: FORM_CONSENT_COPY.terms, action: verb }).replace(
    '{{action}}',
    verb,
  );
  return (
    <Typography
      component="p"
      data-testid="form-consent"
      sx={{
        m: 0,
        fontSize: 12.5,
        lineHeight: 1.5,
        color: `var(${SEMANTIC_CSS_VARS.textMuted})`,
        '& .form-consent-link': {
          color: 'inherit',
          fontWeight: 600,
          textDecoration: 'none',
          '&:hover, &:focus-visible': { textDecoration: 'underline' },
        },
        ...(sx as object),
      }}
    >
      {withConsentLinks(text)}
    </Typography>
  );
}
